import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../services/api';

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  createdAt: string;
  _count?: {
    workouts: number;
    meals: number;
    progress: number;
  };
}

// In-memory cache for clients
let clientCache: Client[] | null = null;
let clientCacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export default function Clients() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // State
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Fetch clients with caching
  const fetchClients = useCallback(async () => {
    // Check cache first
    if (clientCache && clientCacheTimestamp && 
        (Date.now() - clientCacheTimestamp < CACHE_DURATION)) {
      setAllClients(clientCache);
      setFilteredClients(clientCache);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await api.get('/clients');
      const clientList = response.data.clients || response.data;
      
      // Update cache
      clientCache = clientList;
      clientCacheTimestamp = Date.now();
      
      setAllClients(clientList);
      setFilteredClients(clientList);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Filter clients based on search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredClients(allClients);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = allClients.filter(client =>
      client.firstName.toLowerCase().includes(searchLower) ||
      client.lastName.toLowerCase().includes(searchLower)
    );
    setFilteredClients(filtered);
  }, [searchTerm, allClients]);

  // Load clients on mount
  useEffect(() => {
    if (user?.roles?.includes('TRAINER')) {
      fetchClients();
    }
  }, [fetchClients, user]);

  // Authorization check
  if (!user?.roles?.includes('TRAINER')) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-600">Access denied. Trainer access required.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-4">
        <div className="mb-4">
          <h1 className="text-xl font-semibold text-gray-900">My Clients</h1>
          <p className="text-sm text-gray-600">
            {filteredClients.length} of {allClients.length} clients
          </p>
        </div>
        
        {/* Search Bar */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by first or last name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Clients List */}
      <div className="px-4 py-2">
        {filteredClients.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">
              {searchTerm ? 'No clients found matching your search.' : 'No clients assigned to you yet.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredClients.map((client) => (
              <div
                key={client.id}
                className="bg-white rounded-lg border border-gray-200"
              >
                <button
                  onClick={() => navigate(`/trainer/clients/${client.id}`)}
                  className="w-full p-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between"
                >
                  <span className="font-medium text-gray-900">
                    {client.firstName} {client.lastName}
                  </span>
                  <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}